import * as _ from 'lodash';

/**
 * Parameter decorator.
 * Ensures a decorated parameter is a string of length 36, which seems close enough to a UUID for our purposes.
 */
export function isUUID(target: object, propertyKey: string, parameterIndex: number) {
  addValidationMetadata(target, propertyKey, parameterIndex, 'isUUID', ensureUUID);
}

function ensureUUID(param: any, parameterName?: string) {

  ensureNotNull(param);

  if (param.length !== 36) {
    const message = `${parameterName}: Parameter validation failed. Parameter is not a valid UUID: ${param}`;
    console.error(message);
    throw new Error(message);
  }
}

/**
 * Parameter decorator.
 * Ensures a decorated parameter is neither null nor undefined
 */
export function isNotNull(target: object, propertyKey: string, parameterIndex: number) {
  addValidationMetadata(target, propertyKey, parameterIndex, 'isNotNull', ensureNotNull);
}

function ensureNotNull(param: any, parameterName?: string) {
  if (param === null || param === undefined) {
    const message = `${parameterName}: Parameter validation failed. Parameter is null or undefined: ${param}`;
    console.error(message);
    throw new Error(message);
  }
}

/**
 * Parameter decorator.
 * Ensures a decorated parameter is not empty. Array-like values such as arguments objects, arrays,
 * buffers, strings, or jQuery-like collections are considered empty if they have a length of 0. Similarly, maps and
 * sets are considered empty if they have a size of 0.
 */
export function isNotEmpty(target: object, propertyKey: string, parameterIndex: number) {
  addValidationMetadata(target, propertyKey, parameterIndex, 'isNotEmpty', ensureNotEmpty);
}

function ensureNotEmpty(param: any, parameterName?: string) {
  if (_.isEmpty(param)) {
    const message = `${parameterName}: Parameter validation failed. Parameter is empty: ${param}`;
    console.error(message);
    throw new Error(message);
  }
}

/**
 * Parameter decorator.
 * Ensures a decorated parameter is a number and it is non-negative and not NaN
 */
export function isNotNegative(target: object, propertyKey: string, parameterIndex: number) {
  addValidationMetadata(target, propertyKey, parameterIndex, 'isNotNegative', ensureNotNegative);
}

function ensureNotNegative(param: any, parameterName?: string) {
  if (!_.isNumber(param) || Number.isNaN(param) || param < 0) {
    const message = `${parameterName}: Parameter validation failed. Parameter is negative: ${param}`;
    console.error(message);
    throw new Error(message);
  }
}

/**
 * Parameter decorator.
 * Ensures a decorated parameter is a number and it is greater than zero and not NaN
 */
export function isGreaterZero(target: object, propertyKey: string, parameterIndex: number) {
  addValidationMetadata(target, propertyKey, parameterIndex, 'isNotNegative', ensureGreaterZero);
}

function ensureGreaterZero(param: any, parameterName?: string) {
  if (!_.isNumber(param) || Number.isNaN(param) || param <= 0) {
    const message = `${parameterName}: Parameter validation failed. Parameter is not greater Zero: ${param}`;
    console.error(message);
    throw new Error(message);
  }
}

/**
 * This is the key of the member of the prototype where meta-information about validators is stored. The object at this
 * location contains all information needed to validate the actual parmaters for a function call.
 */
const VALIDATOR_KEY = '___parameter_validators';

/**
 * This adds the necessary metadata to validate a call to a member function. The object could look something like this:
 *
 * ___parameter_validators: {
 *     validatorName: {
 *         functionName: {
 *             parameterIndices: [0, 42]
 *             validator: (parameter) => {...}
 *         }
 *     }
 * }
 */
function addValidationMetadata(target: object, propertyKey: string, parameterIndex: number, validatorName: string, validator: Function) {

  const emptyConfiguration = { parameterIndices: [], validator };
  const validatorConfiguration = _.get(target, [VALIDATOR_KEY, validatorName, propertyKey], emptyConfiguration);

  if (validatorConfiguration.parameterIndices.indexOf(parameterIndex) === -1) {
    validatorConfiguration.parameterIndices.push(parameterIndex);
  }

  _.set(target, [VALIDATOR_KEY, validatorName, propertyKey], validatorConfiguration);
}

/**
 * This function does the actual validation. It checks the object for registered validators, and for each validator
 * it calls the corresponding validation function.
 */
export function ValidateParameters(target: object, propertyKey: string, descriptor: PropertyDescriptor): PropertyDescriptor {
  const originalMethod = descriptor.value;

  descriptor.value = function(...args: any[]) {
    for (const validator in target[VALIDATOR_KEY]) {
      if (target[VALIDATOR_KEY].hasOwnProperty(validator)) {
        const validatorConfiguration = _.get(target, [VALIDATOR_KEY, validator, propertyKey], false);
        if (!validatorConfiguration) {
          continue;
        }

        validatorConfiguration.parameterIndices.forEach(i => {
          validatorConfiguration.validator(args[i], propertyKey);
        });
      }
    }
    return originalMethod.apply(this, args);
  };

  return descriptor;
}
